/**
 * Resolve the grading-scheme criterion a learner is shown when they have failed a
 * minimum-passing activity.
 *
 * This is the frontend mirror of `CourseGrade::minimumPassingActivityCheck()`
 * (app/Models/CourseGrade.php). The backend is authoritative — it is what the
 * persisted `CourseGrade`, the transcript and the certificate read — so the
 * gradebook grid must select the same criterion or a student sees one letter in the
 * grid and a different one on their record.
 *
 * Backend algorithm, mirrored here: walk the criteria in `minimum` descending order
 * and take the one immediately *after* the `is_minimum_passing` criterion — the tier
 * directly below passing (D on a Standard A–F scheme, not F) — falling back to the
 * criterion named `Fail` only when the minimum-passing criterion is the last one.
 *
 * A scheme with no `is_minimum_passing` criterion falls through to that same `Fail`
 * lookup, exactly as the backend does. Both sides return null when even that is
 * absent; the difference is what the caller does with it. `CourseGrade::minimumPassingActivityCheck()`
 * throws a RuntimeException because it cannot persist a failing grade without a
 * criterion, whereas the grid renders a placeholder rather than crashing.
 *
 * `resources/js/__tests__/utilities/gradebookFailingThreshold.test.js` and
 * `tests/Unit/Models/GradingSchemeFailingThresholdCriteriaTest.php` both assert
 * against `resources/js/__tests__/fixtures/gradingSchemeFailingThreshold.json`, so
 * the two implementations cannot drift apart silently.
 */

/** Mirrors `GradingSchemeCriteria::PASS_FAIL_FAILING_NAME` — case-sensitive and load-bearing. */
export const PASS_FAIL_FAILING_NAME = 'Fail';

/**
 * @typedef {object} GradingSchemeCriterion
 * @property {string} name
 * @property {number} minimum Fraction, 0.0–1.0
 * @property {boolean} is_minimum_passing
 */

/**
 * The LOWEST criterion in the scheme — F on a Standard scheme.
 *
 * Deliberately not the same thing as `resolveFailingThreshold()`, which returns the tier
 * directly BELOW passing (D). They were the same expression until the threshold fix,
 * which is why the publish-failing-grade modal could display one value and record
 * another without anyone noticing.
 *
 * Be careful what you conclude from this. It is what
 * `submitCourseGrade({ manualFail: true })` POSTS, but not necessarily what is recorded:
 * saving fires SyncCourseGrade -> syncGradingSchemeCriteria(), which runs
 * minimumPassingActivityCheck() BEFORE its `overridden` short-circuit and overwrites the
 * criterion with the failing threshold for a learner who failed a minimum-passing
 * activity. `GradebookBody.publishingGradeFor(student)` resolves which one a given
 * learner actually gets.
 *
 * @param {GradingSchemeCriterion[]|null|undefined} criterias
 * @returns {GradingSchemeCriterion|null}
 */
export function resolveLowestCriteria(criterias) {
    if (!Array.isArray(criterias) || criterias.length === 0) {
        return null;
    }

    return [...criterias].sort((a, b) => Number(b.minimum) - Number(a.minimum)).at(-1) ?? null;
}

/**
 * @param {GradingSchemeCriterion[]|null|undefined} criterias
 * @returns {GradingSchemeCriterion|null} The failing threshold, or null when the scheme
 *   exposes no failing tier at all.
 */
export function resolveFailingThreshold(criterias) {
    if (!Array.isArray(criterias) || criterias.length === 0) {
        return null;
    }

    // The API orders criteria `minimum desc` (GradingSchemeBase::criterias) and the walk
    // below depends on that. Sorted defensively because this is a shared helper and a
    // caller may hand it criteria from somewhere other than the payload — NOT because
    // the payload's order is at risk: Course.js declares `grading_scheme: this.attr(null)`,
    // so it is the raw response, not a normalized Vuex-ORM relation, and its order holds.
    const ordered = [...criterias].sort((a, b) => Number(b.minimum) - Number(a.minimum));

    const minimumPassingIndex = ordered.findIndex(criteria => Boolean(criteria.is_minimum_passing));
    const nextCriteria = minimumPassingIndex === -1 ? undefined : ordered[minimumPassingIndex + 1];

    if (nextCriteria) {
        return nextCriteria;
    }

    // Minimum passing is the lowest tier, or the scheme declares none at all — the
    // backend falls back to an explicit `Fail` criterion in both cases.
    return ordered.find(criteria => !criteria.is_minimum_passing && criteria.name === PASS_FAIL_FAILING_NAME) ?? null;
}
